// Import dayjs locales statically to ensure they're always available
import 'dayjs/locale/hu'
import 'dayjs/locale/en'
import 'dayjs/locale/de'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat' // ES 2015
import localeData from 'dayjs/plugin/localeData'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import weekday from 'dayjs/plugin/weekday'
import i18next, { t } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initLitI18n, translate } from 'lit-i18n'

import de from './locales/de.json'
import en from './locales/en.json'
import hu from './locales/hu.json'

dayjs.extend(localeData)
dayjs.extend(weekday)
dayjs.extend(LocalizedFormat)
dayjs.extend(customParseFormat)

const DEFAULT_LOCALE = 'hu'

const getDefaultLang = () => {
  let lang =
    document.documentElement.lang || navigator.language || DEFAULT_LOCALE
  lang = lang.slice(0, 2)
  return lang
}

/**
 * Tracks which Day.js locales have been loaded.
 * - Built-in locales (de, en, hu) are statically imported and always available.
 * - For other languages, consumers must manually import and register using registerDayjsLocale().
 *
 * Example for adding a custom locale:
 * ```ts
 * import 'dayjs/locale/fr'
 * import { registerDayjsLocale } from 'dap-design-system'
 * registerDayjsLocale('fr')
 * ```
 */
const loadedDayjsLocales = new Set<string>(['hu', 'en', 'de'])

async function ensureDayjsLocale(lang: string) {
  const short = lang.slice(0, 2)

  if (!short) {
    return
  }

  if (!loadedDayjsLocales.has(short)) {
    // Locale not registered - warn user to manually import it
    console.warn(
      `[DAP Design System] Day.js locale "${short}" is not registered. ` +
        `Date/month/day names will not be localized for this language. ` +
        `Falling back to "${DEFAULT_LOCALE}" locale for Day.js. ` +
        `To fix this, manually import and register the locale:\n` +
        `  import 'dayjs/locale/${short}'\n` +
        `  import { registerDayjsLocale } from 'dap-design-system'\n` +
        `  registerDayjsLocale('${short}')`,
    )
  }

  if (loadedDayjsLocales.has(short)) {
    dayjs.locale(short)
  } else {
    dayjs.locale(DEFAULT_LOCALE)
  }
}

i18next
  .use(LanguageDetector)
  .use(initLitI18n)
  .init({
    fallbackLng: 'hu',
    interpolation: {
      escapeValue: false,
    },
    resources: {
      de: {
        translation: {
          ...de,
        },
      },
      en: {
        translation: {
          ...en,
        },
      },
      hu: {
        translation: {
          ...hu,
        },
      },
    },
  })

const initialLang = getDefaultLang()
// Fire and forget – components using Day.js will start rendering with the
// correct locale as soon as the chunk is loaded.
void ensureDayjsLocale(initialLang)
i18next.changeLanguage(initialLang)

const documentElementObserver = new MutationObserver(update)
documentElementObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['dir', 'lang'],
})

export function update() {
  const lang = getDefaultLang()

  void ensureDayjsLocale(lang)
  i18next.changeLanguage(lang)
}

/**
 * Detects whether a locale prefers 12-hour or 24-hour time format.
 * Uses dayjs locale data to determine the preference.
 * @param locale - The locale to check (e.g., 'en', 'hu', 'en-US', 'hu-HU', 'de', 'de-DE')
 * @returns '12h' for 12-hour format, '24h' for 24-hour format
 */
export function getTimeFormatPreference(locale: string): '12h' | '24h' {
  try {
    // Set the locale temporarily to check its format
    const currentLocale = dayjs.locale()
    dayjs.locale(locale)
    const dayjsLocale = dayjs.localeData()
    const longTimeFormat = dayjsLocale.longDateFormat('LT')

    // Restore original locale
    dayjs.locale(currentLocale)

    // If the locale has a defined LT format, check if it contains AM/PM indicators
    if (longTimeFormat && longTimeFormat !== 'LT') {
      // Check if the format contains AM/PM indicators (A or a)
      if (longTimeFormat.includes('A') || longTimeFormat.includes('a')) {
        return '12h'
      }

      // Check if the format uses 24-hour (H:mm) vs 12-hour (h:mm)
      if (longTimeFormat.includes('H:mm') || longTimeFormat.includes('HH:mm')) {
        return '24h'
      }

      if (longTimeFormat.includes('h:mm')) {
        return '12h'
      }
    }

    // If no specific format is found, fall back to common patterns
    const localeLower = locale.toLowerCase()
    if (
      localeLower.startsWith('en') ||
      localeLower.startsWith('us') ||
      localeLower.startsWith('ca') ||
      localeLower.startsWith('au') ||
      localeLower.startsWith('nz') ||
      localeLower.startsWith('ph') ||
      localeLower.startsWith('in') ||
      localeLower.startsWith('pk') ||
      localeLower.startsWith('bd') ||
      localeLower.startsWith('lk') ||
      localeLower.startsWith('np') ||
      localeLower.startsWith('mm') ||
      localeLower.startsWith('my') ||
      localeLower.startsWith('sg')
    ) {
      return '12h'
    }

    // Most other locales use 24-hour format
    return '24h'
  } catch (error) {
    // If locale detection fails, fall back to common patterns
    const localeLower = locale.toLowerCase()
    if (
      localeLower.startsWith('en') ||
      localeLower.startsWith('us') ||
      localeLower.startsWith('ca') ||
      localeLower.startsWith('au') ||
      localeLower.startsWith('nz') ||
      localeLower.startsWith('ph') ||
      localeLower.startsWith('in') ||
      localeLower.startsWith('pk') ||
      localeLower.startsWith('bd') ||
      localeLower.startsWith('lk') ||
      localeLower.startsWith('np') ||
      localeLower.startsWith('mm') ||
      localeLower.startsWith('my') ||
      localeLower.startsWith('sg')
    ) {
      return '12h'
    }

    // Most other locales use 24-hour format
    return '24h'
  }
}

/**
 * Manually register a Day.js locale that has been imported.
 * Use this if automatic locale loading fails.
 *
 * @example
 * ```ts
 * import 'dayjs/locale/fr'
 * import { registerDayjsLocale } from 'dap-design-system'
 * registerDayjsLocale('fr')
 * ```
 */
export function registerDayjsLocale(localeCode: string): void {
  const short = localeCode.slice(0, 2)
  if (short) {
    loadedDayjsLocales.add(short)
    dayjs.locale(short)
  }
}

export { i18next, t, translate }
